import type { FC } from 'react';
import type { CustomTagConfig } from './const';
import { lazy } from 'react';
import { WEGIC_TAGS } from './const';
import { parseOptions } from './OptionSelector/const';

export type { ComponentNode, CustomTagConfig, InlineComponentProps } from './const';
export { createWegicTag, extractTextFromChildren, hasWegicTag, INTERNAL_TAGS, WEGIC_TAGS } from './const';
export { serializeSiteAuditIssuesPayload } from './SiteAuditIssues/const';

/**
 * 懒加载自定义标签组件
 */
const GenStartButton = lazy(() => import('./GenStartButton'));
const CodeErrorContext = lazy(() => import('./CodeErrorContext'));
const DownloadCodeButton = lazy(() => import('./DownloadCodeButton'));
const AssetCollectForm = lazy(() => import('./AssetCollectForm'));
const SiteAuditIssues = lazy(() => import('./SiteAuditIssues'));
const PageStructureCard = lazy(() => import('./PageStructure').then(module => ({ default: module.PageStructureCard })));
const SummaryListCard = lazy(() => import('./SummaryList').then(module => ({ default: module.SummaryListCard })));
const ProjectLaunchOrder = lazy(() => import('./ProjectLaunchOrder'));
const CloudConfirm = lazy(() => import('./CloudConfirm'));

/**
 * 自定义标签配置列表
 */
export const CUSTOM_TAG_CONFIGS: CustomTagConfig[] = [
  {
    tag: WEGIC_TAGS.TRIAL_PAY_BUTTON,
    allowedAttributes: ['label', 'className'],
    component: GenStartButton,
  },
  {
    // 预期统一成 wg-code-error，历史兼容 wg-runtime-error，一段时间后可直接下掉
    tag: WEGIC_TAGS.RUNTIME_ERROR,
    allowedAttributes: ['className'],
    component: CodeErrorContext,
  },
  {
    tag: WEGIC_TAGS.CODE_ERROR,
    allowedAttributes: ['className'],
    component: CodeErrorContext,
  },
  {
    tag: WEGIC_TAGS.SITE_AUDIT_ISSUES,
    allowedAttributes: ['className'],
    component: SiteAuditIssues,
  },
  {
    tag: WEGIC_TAGS.DOWNLOAD_CODE,
    allowedAttributes: ['className'],
    component: DownloadCodeButton,
  },
  {
    tag: WEGIC_TAGS.WEGIC_ASSETS_FORM,
    allowedAttributes: ['className'],
    component: AssetCollectForm,
  },
  {
    tag: WEGIC_TAGS.WEGIC_PAGE_STRUCTURE,
    allowedAttributes: ['className'],
    component: PageStructureCard,
  },
  {
    tag: WEGIC_TAGS.WEGIC_SUMMARY_LIST,
    allowedAttributes: ['className'],
    component: SummaryListCard,
  },
  {
    tag: WEGIC_TAGS.PROJECT_LAUNCH_ORDER,
    allowedAttributes: ['className'],
    component: ProjectLaunchOrder,
  },
  {
    tag: WEGIC_TAGS.CLOUD_CONFIRM,
    allowedAttributes: ['className'],
    component: CloudConfirm,
  },
];

/**
 * 将下划线标签名转换为连字符
 * 自动将所有自定义标签的下划线写法转换为中线写法
 */
export function normalizeCustomTags(content: string): string {
  return CUSTOM_TAG_CONFIGS.reduce((result, { tag }) => {
    // 将 tag 的中线转换为下划线，用于匹配错误写法
    const underscoreName = tag.replace(/-/g, '_');
    const regex = new RegExp(`<(/?)(${underscoreName})([^>]*)>`, 'g');
    return result.replace(regex, `<$1${tag}$3>`);
  }, content);
}

/**
 * 剥掉 markdown 反引号包裹的 trial_pay_button 标签（就多余兜这个底！）
 *
 * AI 偶尔会把 `<trial_pay_button></trial_pay_button>` 包在 inline code 或 code block 里返回，
 */
export function unwrapBacktickedCustomTags(content: string): string {
  const tag = WEGIC_TAGS.TRIAL_PAY_BUTTON;
  const tagAlternatives = `${tag}|${tag.replace(/-/g, '_')}`;

  // 反引号外已存在原始标签时直接返回，反引号内的部分继续以代码形式展示
  const contentOutsideBackticks = content
    .replace(/`{3}[\s\S]*?`{3}/g, '')
    .replace(/`[^`\n]*`/g, '');
  if (new RegExp(`<\\/?(?:${tagAlternatives})\\b`).test(contentOutsideBackticks)) {
    return content;
  }

  // 三反引号 ```lang\n...<trial_pay_button>...\n```
  const fencedRegex = new RegExp(
    `\`{3}[a-zA-Z0-9-]*\\n?([\\s\\S]*?<\\/?(?:${tagAlternatives})\\b[\\s\\S]*?)\\n?\`{3}`,
    'g',
  );

  // 单反引号 `...<trial_pay_button>...`，不跨行
  const inlineRegex = new RegExp(
    `\`([^\`\\n]*<\\/?(?:${tagAlternatives})\\b[^\`\\n]*)\``,
    'g',
  );

  return content
    .replace(fencedRegex, '$1')
    .replace(inlineRegex, '$1');
}

/**
 * code-error 内容来自构建器/运行时原始报错，里面经常包含 JSX/HTML 片段。
 * CodeErrorContext 不展示 children，渲染前直接清空内容，避免错误源码中的 <section> 之类被当成真实 HTML 破坏消息结构。
 */
export function stripCodeErrorTagContent(content: string): string {
  const tags = [WEGIC_TAGS.CODE_ERROR, WEGIC_TAGS.RUNTIME_ERROR];
  const tagAlternatives = tags.flatMap(tag => [tag, tag.replace(/-/g, '_')]).join('|');
  const codeErrorTagRegex = new RegExp(
    `(<(${tagAlternatives})\\b[^>]*>)[\\s\\S]*?(<\\/\\2>)`,
    'gi',
  );

  return content.replace(codeErrorTagRegex, '$1$3');
}

/**
 * 获取所有自定义标签的 HTML 标签名列表
 */
export function getCustomTagNames(): string[] {
  return CUSTOM_TAG_CONFIGS.map(config => config.tag);
}

/**
 * 获取自定义标签的属性配置
 */
export function getCustomTagAttributes(): Record<string, string[]> {
  return CUSTOM_TAG_CONFIGS.reduce((attributes, { tag, allowedAttributes }) => {
    if (allowedAttributes) {
      attributes[tag] = allowedAttributes;
    }
    return attributes;
  }, {} as Record<string, string[]>);
}

/**
 * 生成自定义组件映射
 */
export function generateCustomComponents(): Record<string, FC> {
  return CUSTOM_TAG_CONFIGS.reduce((components, { tag, component }) => {
    components[tag] = component as FC;
    return components;
  }, {} as Record<string, FC>);
}

/**
 * 移除消息中的选项标签，选项只在输入框上方渲染为快捷按钮。
 */
export function removeOptionTags(content: string): string {
  const underscoreTag = WEGIC_TAGS.OPTIONS.replace(/-/g, '_');
  const optionsTagRegex = new RegExp(`<(${WEGIC_TAGS.OPTIONS}|${underscoreTag})[^>]*>([\\s\\S]*?)<\\/(${WEGIC_TAGS.OPTIONS}|${underscoreTag})>`, 'gi');

  return content.replace(optionsTagRegex, '').trim();
}

/** 将历史消息中的选项标签转换为 control 组沿用的无序列表。 */
export function convertOptionTagsToText(content: string): string {
  const underscoreTag = WEGIC_TAGS.OPTIONS.replace(/-/g, '_');
  const optionsTagRegex = new RegExp(`<(${WEGIC_TAGS.OPTIONS}|${underscoreTag})[^>]*>([\\s\\S]*?)<\\/(${WEGIC_TAGS.OPTIONS}|${underscoreTag})>`, 'gi');

  return content.replace(optionsTagRegex, (_match, _tag1, innerContent) => {
    try {
      const options = parseOptions(innerContent);
      return options.length > 0
        ? `\n${options.map(option => `- ${option.label}`).join('\n')}\n`
        : '';
    } catch {
      return '';
    }
  });
}
